/**
 * eSign — Public signing page. Opened by recipients via a tokenized link
 * (/esign/sign/:token). No auth: the token is the credential.
 *
 * After the recipient finishes, we do NOT wipe the page — matching eSign we keep the
 * document visible with every field rendered read-only ("Signed by …"), show a
 * "You've signed" banner, and offer a Download of the completed PDF.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, PenLine, Loader2, Download, ArrowRight, ShieldCheck } from 'lucide-react';
import esignApi from '../../../api/esign';
import PdfCanvas from '../components/PdfCanvas';
import { DOC_CANVAS } from '../../../components/ui/documentCanvas';
import SignatureModal from '../components/SignatureModal';
import { byReadingOrder } from '../signingWalk';
import collingsLogo from '../../../assets/collingslogo.png';

const SIGNATURE_TYPES = ['signature', 'initial'];

export default function EsignSigningPage() {
  const { token } = useParams();
  // In-person handover: the agent opened this on their own device from the
  // authority form (`?agent=1`), so once done we offer a way back to the CRM.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agentMode = searchParams.get('agent') === '1';
  // Where to send the agent after an in-person signature (defaults to the agreements
  // list; the per-party launcher passes the authority's detail page so the agent can
  // launch the next party).
  const returnTo = searchParams.get('return') || '/esign/agreements';
  const [envelope, setEnvelope] = useState(null);
  const [signReq, setSignReq] = useState(null);
  const [fields, setFields] = useState([]);
  const [contextFields, setContextFields] = useState([]);
  const [error, setError] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [signed, setSigned] = useState(false);
  const [sigModal, setSigModal] = useState(null);
  const [approved, setApproved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // RTA-style: greet the vendor first, then let them tap into the document — rather
  // than dumping a PDF at them (the DocuSign feel). `started` gates the cover screen.
  const [started, setStarted] = useState(false);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  // Banking / Body corporate / Legal representation - vendor self-entry, only
  // applicable on the vendor's own token (not the agent's), see backend gate.
  const [capturedSchema, setCapturedSchema] = useState(null);
  const [capturedValues, setCapturedValues] = useState({});
  // Guided walk (DocuSign-style): after a signature is adopted we carry the signer
  // to the next required field so they can read that section before signing it
  // (DOMA 13272162455). `pendingAdvance` is armed on adopt and consumed by the
  // effect once the just-signed field drops out of `unfilled`; `unfilledRef`
  // hands the effect the freshly recomputed walk order without re-deriving it.
  const pendingAdvanceRef = useRef(false);
  const unfilledRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const { data } = await esignApi.getSigning(token);
      setEnvelope(data.envelope);
      setSignReq(data.sign_request);
      const signerEmail = data.sign_request.signer_email;
      const toPersist = [];
      const seeded = (data.sign_request.fields || []).map((f) => {
        if (f.type === 'email' && !f.value && signerEmail) {
          toPersist.push(f.field_id);
          return { ...f, value: signerEmail };
        }
        return f;
      });
      setFields(seeded);
      // Read-only values earlier signers already filled in (sequential/template flow).
      setContextFields(data.context_fields || []);
      // Persist the auto-filled email to the server, else the completion check still sees it empty.
      if (data.sign_request.status !== 'signed') {
        toPersist.forEach((fid) => esignApi.setFieldValue(token, fid, signerEmail).catch(() => {}));
      }
      if (data.sign_request.approved_at) setApproved(true);
      if (data.sign_request.status === 'signed') setSigned(true);
      if (data.envelope.status === 'voided') { setDeclined(true); }
      // Only the vendor's own token (order >= 2) gets a non-null schema back;
      // the agent's own signing session just gets {applicable: false}.
      if (data.sign_request.order >= 2) {
        esignApi.getCapturedDetails(token).then(({ data: cd }) => {
          if (cd.applicable) {
            setCapturedSchema(cd.schema);
            setCapturedValues(cd.values || {});
          }
        }).catch(() => {});
      }
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(code === 'LINK_EXPIRED' ? 'This signing link has expired.' : 'This link is invalid.');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Load the PDF *through the API* as a blob (same-origin, HTTPS) rather than letting
  // react-pdf fetch the raw S3 URL — which the browser blocks on the https prod page.
  // Re-runs when the envelope status flips (pending -> completed) so the baked/signed
  // copy replaces the original once signing finishes.
  const envStatus = envelope?.status;
  const envFileUrl = envelope?.file_url;
  useEffect(() => {
    // Wait for the envelope metadata to arrive first, so we only make ONE PDF request
    // (and have the fallback URL ready) — avoids a "No document" flash before the real load.
    if (!envFileUrl) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setPdfError(false);
    (async () => {
      try {
        const { data } = await esignApi.signingFileBlob(token);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setPdfSrc(objectUrl);
      } catch {
        // Stream endpoint unavailable (e.g. backend not yet redeployed) — fall back to
        // the direct file URL so the viewer still works wherever the browser allows it.
        if (!cancelled) setPdfSrc(envFileUrl);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [token, envStatus, envFileUrl]);

  const updateLocal = (fieldId, patch) =>
    setFields((fs) => fs.map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f)));

  const onValue = async (field, value) => {
    updateLocal(field.field_id, { value });
    try { await esignApi.setFieldValue(token, field.field_id, value); }
    catch { toast.error('Could not save value.'); }
  };

  const onAdoptSignature = async (data) => {
    const field = sigModal;
    updateLocal(field.field_id, { signature_data: data });
    // Arm the guided walk: the fields change below re-renders, and the effect
    // then carries the signer to the next required field to read before signing.
    pendingAdvanceRef.current = true;
    try { await esignApi.signField(token, field.field_id, data); }
    catch { toast.error('Could not save signature.'); }
  };

  const complete = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Flush every current value/signature to the server first, so no unsaved local state
      // (e.g. the auto-filled email) makes the completion check think a field is empty.
      // A rejected flush used to be swallowed entirely, so a signer whose writes were
      // all being refused still saw "All done - ready to sign off" with nothing saved.
      // Only a 409 blocks us here (the server is refusing this signer's writes
      // outright); a one-off network error on a single field is left to the
      // completion check below, which reports any field that really is missing.
      let refused = false;
      await Promise.all(fields.map((f) => {
        const failed = (err) => { if (err?.response?.status === 409) refused = true; };
        if (SIGNATURE_TYPES.includes(f.type)) {
          return f.signature_data ? esignApi.signField(token, f.field_id, f.signature_data).catch(failed) : null;
        }
        if (['note', 'line', 'approve', 'decline'].includes(f.type)) return null;
        // Read-only fields can't be changed by the signer and the server rejects
        // writes to them (FIELD_READ_ONLY), so there's nothing to flush - their
        // value is the sender's frozen default already on the server.
        if (f.read_only) return null;
        return esignApi.setFieldValue(token, f.field_id, f.value || '').catch(failed);
      }).filter(Boolean));
      if (refused) {
        // Don't report success on a document whose entries never reached the server.
        toast.error('This document is no longer accepting changes on this link. Please contact your agent.');
        await load();
        return;
      }
      await esignApi.complete(token);
      toast.success('Document signed.');
      await load(); // refresh: sign-request now signed, envelope may be completed (download ready)
      setSigned(true);
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === 'MISSING_REQUIRED_FIELDS') {
        toast.error('Please complete all required fields before finishing.');
      } else if (code === 'ALREADY_ACTIONED') {
        // Never show a signer the raw code (Simon Abbott, 2026-09-09: a buyer was
        // left staring at a red "ALREADY_ACTIONED"). Re-load instead, so the page
        // flips to the real "You've signed" state rather than keeping a dead button.
        toast.error('This document has already been signed on this link.');
        await load();
      } else if (code === 'NOT_SIGNABLE') {
        toast.error('This document is no longer open for signing. Please contact your agent.');
        await load();
      } else {
        toast.error(
          typeof code === 'string' && /^[A-Z_]+$/.test(code)
            ? 'Could not complete signing. Please try again, or contact your agent.'
            : code || 'Could not complete.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    if (!window.confirm('Decline to sign this document? A VOID watermark will be applied and this cannot be undone.')) return;
    try { await esignApi.decline(token); setDeclined(true); }
    catch { toast.error('Could not decline.'); }
  };

  const saveCapturedField = async (key, value) => {
    setCapturedValues((v) => ({ ...v, [key]: value }));
    try { await esignApi.saveCapturedDetails(token, { [key]: value }); }
    catch { toast.error('Could not save that.'); }
  };

  const approve = async () => {
    try { await esignApi.approve(token); setApproved(true); toast.success('Approved.'); }
    catch { toast.error('Could not approve.'); }
  };

  const download = async () => {
    // Download the baked, pre-filled PDF (values + agent signature rendered in),
    // not the blank original — an older vendor needs to read the completed
    // authority on paper before signing (CG/Simon). Once signed, this serves the
    // finalised signed copy.
    try {
      const res = await esignApi.signingPreviewBlob(token);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${envelope?.title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      const url = envelope?.file_url;
      if (url) window.open(url, '_blank');
      else toast.error('Could not prepare the document for download. Please try again.');
    }
  };

  // Engage a field once it's on screen: open the signature pad for a signature/
  // initial, or focus the input for a typed field.
  const engageField = useCallback((field) => {
    const el = document.getElementById(`fld-${field.field_id}`);
    if (!el) return;
    if (SIGNATURE_TYPES.includes(field.type)) setSigModal(field);
    else if (typeof el.focus === 'function') el.focus();
  }, []);

  // Scroll a field into view and pulse a ring around it. Handles PdfCanvas's
  // windowed rendering: a field on an off-screen page has no DOM node yet, so we
  // scroll that page in first (by its data-page-number) and poll until the field
  // mounts. `act: true` then engages the field (sign/focus); the post-adopt walk
  // passes `act: false` so the signer can READ the section before signing.
  const scrollToField = useCallback((field, { act = false } = {}) => {
    if (!field) return;
    const reveal = (el) => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-4', 'ring-teal-300', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-4', 'ring-teal-300', 'ring-offset-2'), 1600);
      if (act) setTimeout(() => engageField(field), 350);
    };
    const existing = document.getElementById(`fld-${field.field_id}`);
    if (existing) { reveal(existing); return; }
    const pageEl = document.querySelector(`[data-page-number="${field.page}"]`);
    if (pageEl) pageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    let tries = 0;
    const poll = () => {
      const el = document.getElementById(`fld-${field.field_id}`);
      if (el) { reveal(el); return; }
      if (tries++ < 40) setTimeout(poll, 100); // wait up to ~4s for the page to mount
    };
    setTimeout(poll, 150);
  }, [engageField]);

  // After a signature is adopted, the just-signed field drops out of `unfilled`
  // and this fires: carry the signer to the next required field and highlight it,
  // WITHOUT opening the pad, so they read that section before signing (the pad
  // opens when they tap the field or the "Sign here" button). Guarded by the
  // adopt flag so typing in other fields never triggers a jump.
  useEffect(() => {
    if (!pendingAdvanceRef.current) return;
    pendingAdvanceRef.current = false;
    const next = unfilledRef.current[0];
    if (next) setTimeout(() => scrollToField(next, { act: false }), 250);
  }, [fields, scrollToField]);

  if (error) return <Centered><XCircle className="w-12 h-12 text-red-400 mb-3" /><p className="text-gray-600">{error}</p></Centered>;
  if (declined) return <Centered><XCircle className="w-12 h-12 text-red-400 mb-3" /><h2 className="text-xl font-semibold">Document declined</h2><p className="text-gray-500">This document has been declined and voided.</p></Centered>;
  if (!envelope) return <Centered><p className="text-gray-400">Loading…</p></Centered>;

  const signedReadOnly = signed;
  const envCompleted = envelope.status === 'completed';

  // Select a single radio circle; if it belongs to a group, clear the others in that group.
  const selectRadio = (f) => {
    const group = f.group_name;
    const toClear = group ? fields.filter((x) => x.type === 'radio' && x.group_name === group && x.field_id !== f.field_id) : [];
    setFields((fs) => fs.map((x) => {
      if (x.field_id === f.field_id) return { ...x, value: 'true' };
      if (toClear.some((c) => c.field_id === x.field_id)) return { ...x, value: '' };
      return x;
    }));
    esignApi.setFieldValue(token, f.field_id, 'true').catch(() => {});
    toClear.forEach((c) => esignApi.setFieldValue(token, c.field_id, '').catch(() => {}));
  };

  const renderField = (f, pageHeight = 0, pageWidth = 0, forceReadOnly = false) => {
    const readOnly = signedReadOnly || forceReadOnly;
    const style = { left: `${f.x_pct}%`, top: `${f.y_pct}%`, width: `${f.w_pct}%`, height: `${f.h_pct}%` };
    // Font scales with the field's pixel height (bigger field -> bigger text).
    const fieldH = (f.h_pct / 100) * (pageHeight || 0);
    const fieldW = (f.w_pct / 100) * (pageWidth || 0);
    const fontPx = Math.max(8, Math.min(fieldH * 0.6, 44));
    // For value-bearing fields, the height-only size can be far too big for a long
    // value (e.g. "$790,000" in a tall field), so it wrapped and overflow-hidden
    // clipped everything but the first digit or two. Mirror the baked PDF, which
    // shrinks the size until the text fits the field width (pdf_service._insert_text).
    // ~0.6em per Helvetica glyph, less the px-1 padding either side.
    const fitFont = (val) => {
      const len = String(val ?? '').length;
      if (!fieldW || !len) return fontPx;
      return Math.max(8, Math.min(fontPx, (fieldW - 6) / (len * 0.6)));
    };
    // A real checkbox/radio is a small SQUARE — never stretched to the field box's
    // aspect ratio (which is often a wide fill-line). Centred inside the field area.
    const glyphSide = Math.max(13, Math.min(fieldW || 18, fieldH || 18, 24));
    const filled = SIGNATURE_TYPES.includes(f.type) ? !!f.signature_data : !!f.value;
    // In the finished (read-only) view, drop the field box entirely — only the content remains.
    const box = readOnly ? '' : `border-2 rounded ${filled ? 'border-green-400 bg-green-50/60' : 'border-teal-400 bg-teal-50/60'}`;

    // Signature / initial
    if (SIGNATURE_TYPES.includes(f.type)) {
      if (f.signature_data) {
        const inner = f.signature_data.startsWith('data:')
          ? <img src={f.signature_data} alt="signature" className="max-w-full max-h-full object-contain" />
          : <span style={{ fontFamily: 'Dancing Script, cursive', fontSize: fontPx }} className="leading-none truncate">{f.signature_data.replace(/^type:/, '').replace(/^typed::[^:]*::/, '')}</span>;
        return readOnly
          ? <div key={f.field_id} className="absolute flex items-center justify-center overflow-hidden p-[1px]" style={style}>{inner}</div>
          : <button key={f.field_id} id={`fld-${f.field_id}`} className={`absolute flex items-center justify-center overflow-hidden p-[1px] rounded ${box}`} style={style} onClick={() => setSigModal(f)}>{inner}</button>;
      }
      return (
        <button key={f.field_id} id={`fld-${f.field_id}`} className={`absolute flex items-center justify-center ${box}`} style={style} onClick={() => setSigModal(f)}>
          <span className="text-teal-700 flex items-center gap-1" style={{ fontSize: Math.min(fontPx, 13) }}>
            <PenLine className="w-3 h-3" /> {f.type === 'initial' ? 'Initial' : 'Sign'}
          </span>
        </button>
      );
    }

    // Checkbox — a real native checkbox, centred in the field area. A field the
    // sender marked read only (e.g. a pre-ticked box) is locked here, so the
    // signer sees it but cannot change it.
    if (f.type === 'checkbox') {
      const checked = String(f.value).toLowerCase() === 'true';
      const locked = readOnly || f.read_only;
      return (
        <div key={f.field_id} className="absolute flex items-center justify-center" style={style}>
          <input type="checkbox" checked={checked}
            onChange={locked ? undefined : () => onValue(f, checked ? '' : 'true')}
            readOnly={locked}
            title={f.read_only ? (f.caption || 'Checkbox') + ' (locked)' : (f.caption || 'Checkbox')}
            className={`accent-teal-600 ${locked ? 'pointer-events-none' : 'cursor-pointer'}`}
            style={{ width: glyphSide, height: glyphSide }} />
        </div>
      );
    }

    // Radio — a real circle, centred in the field area (never an ellipse).
    if (f.type === 'radio') {
      const selected = String(f.value).toLowerCase() === 'true';
      if (readOnly) {
        return (
          <div key={f.field_id} className="absolute flex items-center justify-center" style={style}>
            {selected && <span className="rounded-full bg-gray-900" style={{ width: glyphSide * 0.55, height: glyphSide * 0.55 }} />}
          </div>
        );
      }
      return (
        <div key={f.field_id} className="absolute flex items-center justify-center" style={style}>
          <button type="button" onClick={() => selectRadio(f)}
            className={`rounded-full border-2 flex items-center justify-center ${selected ? 'border-teal-500 bg-teal-50/60' : 'border-teal-400 bg-white/60'}`}
            style={{ width: glyphSide, height: glyphSide }}>
            {selected && <span className="rounded-full bg-teal-600" style={{ width: '55%', height: '55%' }} />}
          </button>
        </div>
      );
    }

    // Dropdown
    if (f.type === 'dropdown') {
      if (readOnly) return <StaticVal key={f.field_id} style={style} value={f.value} fontPx={fitFont(f.value)} />;
      return (
        <select key={f.field_id} value={f.value || ''} onChange={(e) => onValue(f, e.target.value)}
                className={`absolute px-1 ${box}`} style={{ ...style, fontSize: fontPx }}>
          <option value="">-- select --</option>
          {(f.options || []).map((o, i) => <option key={i} value={o.value ?? o.label}>{o.label}</option>)}
        </select>
      );
    }

    // Note (screen-only; not part of the finished document)
    if (f.type === 'note') {
      if (readOnly) return null;
      return (
        <div key={f.field_id} className="absolute rounded bg-amber-50 border border-amber-200 text-amber-800 p-1 overflow-auto"
             style={{ ...style, fontSize: Math.min(fontPx, 13) }}>
          {f.note_content}
        </div>
      );
    }

    // Line — drawn between its stored endpoints (matches the editor + baked PDF).
    if (f.type === 'line') {
      return (
        <svg key={f.field_id} className="absolute overflow-visible" style={style}
          viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <line
            x1={f.x1_pct != null ? ((f.x1_pct - f.x_pct) / (f.w_pct || 1)) * 100 : 0}
            y1={f.y1_pct != null ? ((f.y1_pct - f.y_pct) / (f.h_pct || 1)) * 100 : 50}
            x2={f.x2_pct != null ? ((f.x2_pct - f.x_pct) / (f.w_pct || 1)) * 100 : 100}
            y2={f.y2_pct != null ? ((f.y2_pct - f.y_pct) / (f.h_pct || 1)) * 100 : 50}
            stroke="#111" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
        </svg>
      );
    }

    // Approve / Decline (hidden once signed)
    if (f.type === 'approve') {
      if (readOnly) return null;
      return (
        <button key={f.field_id} onClick={approve} disabled={approved}
          className={`absolute border-2 rounded text-[10px] font-medium ${approved ? 'bg-green-100 border-green-400 text-green-700' : 'bg-teal-50 border-teal-400 text-teal-700'}`} style={style}>
          {approved ? 'Approved' : 'Approve'}
        </button>
      );
    }
    if (f.type === 'decline') {
      if (readOnly) return null;
      return (
        <button key={f.field_id} onClick={decline} className="absolute border-2 rounded text-[10px] font-medium bg-red-50 border-red-400 text-red-600" style={style}>Decline</button>
      );
    }

    // Text — exception: fixed readable font that WRAPS to new lines at the field edge (multi-line).
    if (f.type === 'text') {
      if (readOnly || f.read_only) {
        return (
          <div key={f.field_id} className="absolute px-1 text-gray-900 overflow-hidden whitespace-pre-wrap break-words leading-tight"
               style={{ ...style, fontSize: 13 }}>{f.value || f.default_value}</div>
        );
      }
      return (
        <textarea key={f.field_id} id={`fld-${f.field_id}`} value={f.value || ''}
          onChange={(e) => updateLocal(f.field_id, { value: e.target.value })}
          onBlur={(e) => onValue(f, e.target.value)}
          placeholder={f.placeholder || 'Text'}
          className={`absolute px-1 resize-none leading-tight ${box}`}
          style={{ ...style, fontSize: 13 }} />
      );
    }

    // Other typed inputs — font scales with field size; borderless in the finished view.
    if (readOnly || f.read_only) {
      const v = f.value || f.default_value;
      return <StaticVal key={f.field_id} style={style} value={v} fontPx={fitFont(v)} />;
    }
    const inputType = (f.type === 'date' || f.type === 'date_dmy') ? 'date' : f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'mobile' ? 'tel' : 'text';
    return (
      <input key={f.field_id} id={`fld-${f.field_id}`} type={inputType}
        value={f.value || ''} onChange={(e) => updateLocal(f.field_id, { value: e.target.value })}
        onBlur={(e) => onValue(f, e.target.value)}
        placeholder={f.placeholder || (f.type === 'name' ? (f.name_format || 'Name') : f.type)}
        className={`absolute px-1 leading-tight ${box}`} style={{ ...style, fontSize: fitFont(f.value) }} />
    );
  };

  // Required fields still needing input. note/line/approve/decline never block;
  // a required checkbox must be ticked and a required radio group needs one
  // option chosen (matches the backend _required_unfilled).
  const isTicked = (v) => ['true', '1', 'yes', 'on'].includes(String(v || '').trim().toLowerCase());
  const requiredInteractive = fields.filter(
    (f) => f.required && !['radio', 'note', 'line', 'approve', 'decline'].includes(f.type)
  );
  const unfilledFields = requiredInteractive.filter((f) =>
    f.type === 'checkbox'
      ? !isTicked(f.value)
      : SIGNATURE_TYPES.includes(f.type) ? !f.signature_data : !(f.value && String(f.value).trim())
  );
  // Required radio groups with no selection, represented by their first field
  // so "go to next" can scroll to them.
  const radioGroups = {};
  fields.forEach((f) => {
    if (f.type === 'radio') {
      // An ungrouped radio is its own single-member group (keyed by field_id),
      // so a lone required radio is still validated - otherwise it slips through
      // both requiredInteractive (which excludes radios) and the grouped check.
      const key = f.group_name || `__ungrouped_${f.field_id}`;
      const g = radioGroups[key] || { required: false, selected: false, first: f };
      g.required = g.required || !!f.required;
      g.selected = g.selected || isTicked(f.value);
      radioGroups[key] = g;
    }
  });
  const unfilledRadio = Object.values(radioGroups).filter((g) => g.required && !g.selected).map((g) => g.first);
  // Reading order (page then y then x) so the walk follows the document rather than
  // the order fields were placed in the editor — otherwise a signature added earlier
  // but sitting lower on the page is visited out of turn.
  const unfilled = [...unfilledFields, ...unfilledRadio].sort(byReadingOrder);
  unfilledRef.current = unfilled;
  const hasApprove = fields.some((f) => f.type === 'approve');
  // All required fields filled, AND an approval given when the doc asks for one.
  // Approving must not skip an unfilled required signature/field on the same doc.
  const canFinish = unfilled.length === 0 && (!hasApprove || approved);

  // The guide-bar button: an explicit "take me to the next field and let me act on
  // it". Unlike the post-adopt walk, this engages the field (opens the pad / focuses
  // the input) since the signer chose to act. Finishes the document when nothing's left.
  const goToNext = () => {
    const next = unfilled[0];
    if (!next) { if (canFinish && !submitting) complete(); return; }
    scrollToField(next, { act: true });
  };

  const sigCount = fields.filter((f) => SIGNATURE_TYPES.includes(f.type)).length;
  const agentName = envelope.owner_name || 'Your agent';
  const agencyName = envelope.agency_name || 'Collings Real Estate';
  const agentInitials = (envelope.owner_name || 'C').split(/\s+/).filter(Boolean)
    .map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  // ---- RTA-style cover: greet the vendor before showing the document ----
  if (!started && !signed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
        <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-center px-5 py-10">
          <div className="flex justify-center mb-7">
            <img src={collingsLogo} alt="Collings" className="h-5 w-auto object-contain opacity-90" />
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-300/40 overflow-hidden">
            {/* teal header — the personal "who sent this" */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-500 text-white px-7 pt-7 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-full bg-white/20 ring-1 ring-white/30 grid place-items-center font-bold text-sm">
                  {agentInitials}
                </div>
                <div className="leading-tight min-w-0">
                  <div className="font-semibold truncate">{agentName}</div>
                  <div className="text-white/75 text-xs truncate">{agencyName}</div>
                </div>
              </div>
              <h1 className="text-[26px] font-bold leading-tight" style={{ textWrap: 'balance' }}>
                Please review &amp; sign
              </h1>
              <p className="text-white/85 text-sm mt-1.5 truncate">{envelope.title}</p>
            </div>

            {/* body */}
            <div className="px-7 py-6">
              {envelope.message && (
                <p className="text-gray-600 text-sm leading-relaxed mb-5 border-l-2 border-teal-300 pl-3.5">
                  {envelope.message}
                </p>
              )}
              <div className="flex items-center gap-3 rounded-2xl bg-teal-50/70 px-4 py-3.5 mb-6">
                <span className="w-10 h-10 rounded-xl bg-white text-teal-600 grid place-items-center shrink-0 shadow-sm">
                  <PenLine className="w-5 h-5" />
                </span>
                <div className="text-sm text-gray-700">
                  <b className="text-gray-900">{sigCount || 1} signature{(sigCount || 1) !== 1 ? 's' : ''}</b> to add
                  <span className="block text-gray-400 text-xs">We'll take you straight to each one.</span>
                </div>
              </div>
              <button onClick={() => setStarted(true)}
                className="w-full bg-teal-600 hover:bg-teal-700 active:scale-[.99] transition text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 shadow-lg shadow-teal-600/25">
                Review &amp; Sign <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={decline}
                className="w-full text-gray-400 hover:text-red-500 text-sm mt-2.5 py-2 transition">
                Decline to sign
              </button>
            </div>
          </div>

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 mt-6">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure signing · your private link expires in 7 days
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-gray-400">{signed ? 'Signed document' : 'Please review & sign'}</div>
          <div className="font-semibold text-gray-900 truncate">{envelope.title}</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {signed ? (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium">
                <CheckCircle className="w-4 h-4" /> Signed
              </span>
              <button onClick={download} className="flex items-center gap-2 text-sm px-4 py-2 rounded border">
                <Download className="w-4 h-4" /> Download
              </button>
            </>
          ) : (
            <>
              {!canFinish && unfilled.length > 0 && (
                <span className="text-xs text-amber-600">{unfilled.length} required field(s) left</span>
              )}
              <button onClick={decline} disabled={submitting}
                      className="text-sm px-4 py-2 rounded border text-red-600 disabled:opacity-50">Decline</button>
              <button onClick={complete} disabled={!canFinish || submitting}
                      className="text-sm px-5 py-2 rounded bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 flex items-center gap-2"
                      title={!canFinish ? 'Complete all required fields first' : ''}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Finishing…' : 'Finish & Sign'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Signed banner */}
      {signed && (
        <div className="max-w-3xl mx-auto mt-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <div className="text-sm text-green-800 flex-1">
            <span className="font-medium">You've signed this document.</span>{' '}
            {envCompleted
              ? 'The document is complete. Download your copy below or from the email we sent.'
              : "We'll email you the completed document once all recipients have signed."}
          </div>
          {envCompleted && (
            <button onClick={download} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded bg-green-600 text-white shrink-0">
              <Download className="w-4 h-4" /> Download signed document
            </button>
          )}
        </div>
      )}

      {/* In-person handover: give the AGENT a way back to the CRM once the vendor
          has signed on their device. Only shown in agent mode (?agent=1). */}
      {agentMode && signed && (
        <div className="max-w-3xl mx-auto mt-3 text-center">
          <button onClick={() => navigate(returnTo)}
            className="text-sm px-4 py-2 rounded-lg border border-teal-600 text-teal-700 hover:bg-teal-50">
            {returnTo === '/esign/agreements' ? 'Return to eSign' : 'Back to signing options'}
          </button>
        </div>
      )}

      {/* Sender message (only before signing) */}
      {!signed && envelope.message && (
        <div className="max-w-3xl mx-auto mt-4 bg-white border rounded-lg px-4 py-3 text-sm text-gray-600">{envelope.message}</div>
      )}

      {/* Banking / Body corporate / Legal representation - vendor self-entry.
          Purely supplementary (never printed on the authority, never gates
          signing) - shown whenever the backend says this token's session is
          applicable (the vendor's own token, not the agent's). */}
      {capturedSchema && (
        <CapturedDetailsSection
          schema={capturedSchema}
          values={capturedValues}
          readOnly={signedReadOnly}
          onFieldBlur={saveCapturedField}
        />
      )}

      {/* Document — darkened canvas behind the page, word-editor style, so the
          white document stands out from the app background. */}
      <div className={`${DOC_CANVAS} mx-4 sm:mx-6 my-6 rounded-2xl p-4 sm:p-6`}>
        <PdfCanvas
          fileUrl={pdfSrc}
          loading={!pdfSrc && !pdfError}
          width={780}
          // When the envelope is completed/voided, the served file is the BAKED pdf (fields
          // already rendered into it) — so don't overlay the fields again (double image).
          renderOverlay={(envelope.status === 'completed' || envelope.status === 'voided')
            ? undefined
            : ({ pageNumber, width, height }) => (
              <>
                {/* Earlier signers' entries, shown read-only so this signer sees the updated doc. */}
                {contextFields.filter((f) => f.page === pageNumber).map((f) => (
                  <React.Fragment key={`ctx-${f.field_id}`}>{renderField(f, height, width, true)}</React.Fragment>
                ))}
                {fields.filter((f) => f.page === pageNumber).map((f) => renderField(f, height, width))}
              </>
            )}
        />
      </div>

      {/* RTA-style guide bar: always visible, walks the vendor to each field and
          turns into the sign-off button once everything's filled. */}
      {!signed && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-white/95 backdrop-blur px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="flex-1 text-sm">
              {unfilled.length ? (
                <span className="text-gray-600">
                  <b className="text-gray-900">{unfilled.length}</b> thing{unfilled.length !== 1 ? 's' : ''} left for you
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-green-600 font-medium">
                  <CheckCircle className="w-4 h-4" /> All done — ready to sign off
                </span>
              )}
            </div>
            <button onClick={goToNext} disabled={submitting || (!unfilled.length && !canFinish)}
              className="px-6 py-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {unfilled.length
                ? (<>{SIGNATURE_TYPES.includes(unfilled[0].type) ? 'Sign here' : 'Next'} <ArrowRight className="w-4 h-4" /></>)
                : (submitting ? 'Finishing…' : 'Finish & Sign')}
            </button>
          </div>
        </div>
      )}

      <SignatureModal open={!!sigModal} onClose={() => setSigModal(null)}
        onAdopt={onAdoptSignature} defaultName={signReq?.signer_name}
        initialsOnly={sigModal?.type === 'initial'} sealId={token} />
    </div>
  );
}

// Banking / Body corporate / Legal representation, filled in by the vendor
// themselves. Mirrors CapturedDetailsPanel.js's grouping (same schema shape),
// simplified for the public page: no edit/view toggle - the vendor either
// hasn't filled it in yet (blank inputs) or has (pre-filled, still editable
// until they've signed). Auto-saves per field on blur, like the rest of this
// page's typed inputs.
function CapturedDetailsSection({ schema, values, readOnly, onFieldBlur }) {
  const [local, setLocal] = useState(values);
  useEffect(() => { setLocal(values); }, [values]);

  const inputType = (t) => (t === 'date' ? 'date' : t === 'email' ? 'email' : t === 'tel' ? 'tel' : 'text');

  return (
    <div className="max-w-3xl mx-auto mt-4 bg-white border rounded-xl px-5 py-5">
      <h2 className="text-sm font-semibold text-gray-900">Your details</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-4">
        Optional — held against your sale record, not printed on this document. Your agent can also fill these in for you.
      </p>
      <div className="space-y-5">
        {(schema.sections || []).map((s) => (
          <section key={s.key}>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{s.title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
              {s.questions.map((q) => (
                <label key={q.key} className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">{q.label}</span>
                  {readOnly ? (
                    <div className="text-sm text-gray-900 py-1.5">{local[q.key] || <span className="text-gray-300">—</span>}</div>
                  ) : (
                    <input
                      type={inputType(q.type)}
                      value={local[q.key] ?? ''}
                      onChange={(e) => setLocal((v) => ({ ...v, [q.key]: e.target.value }))}
                      onBlur={(e) => onFieldBlur(q.key, e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                    />
                  )}
                  {q.help && <span className="block text-[11px] text-gray-400 mt-0.5">{q.help}</span>}
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function StaticVal({ style, value, fontPx }) {
  return (
    <div className="absolute flex items-center px-1 text-gray-900 overflow-hidden leading-tight whitespace-nowrap"
         style={{ ...style, fontSize: fontPx || 12 }}>
      {value}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-gray-50">
      {children}
    </div>
  );
}
